import { useEffect, useState, useCallback } from 'react';
import type { Confession } from '../types/confession';
import { getDailyTop } from '../lib/api';
import { ConfessionCard } from './ConfessionCard';
import { CommentsModal } from './CommentsModal';

interface DailyTopProps {
  onLoadedIds?: (ids: string[]) => void;
}

export function DailyTop({ onLoadedIds }: DailyTopProps) {
  const [confessions, setConfessions] = useState<Confession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [modalConfession, setModalConfession] = useState<{ id: string; commentCount: number } | null>(null);

  const handleOpenComments = useCallback((confessionId: string, commentCount: number) => {
    setModalConfession({ id: confessionId, commentCount });
  }, []);

  const handleCloseComments = useCallback(() => {
    setModalConfession(null);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getDailyTop();
        const top3 = data.slice(0, 3);
        setConfessions(top3);
        // Report loaded IDs to parent so main feed can exclude them
        if (onLoadedIds) {
          onLoadedIds(top3.map(c => c.id));
        }
      } catch (error) {
        console.error('Failed to load daily top:', error);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [onLoadedIds]);

  if (isLoading) {
    return (
      <div className="mb-6">
        <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
          🏆 <span className="skeleton w-32 h-5 rounded" />
        </h3>
        <div className="skeleton h-32 rounded-2xl" />
      </div>
    );
  }

  if (confessions.length === 0) {
    return null;
  }

  return (
    <div className="mb-8">
      {/* Featured section container */}
      <div className="bg-gradient-to-b from-yellow-500/10 to-transparent rounded-2xl p-4 border border-yellow-500/20">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold flex items-center gap-2">
            🏆 Today's Top Confessions
          </h3>
          <span className="text-xs text-yellow-400/70 bg-yellow-500/10 px-2 py-1 rounded-full">
            Featured
          </span>
        </div>
        <div className="space-y-2">
          {confessions.map((confession) => (
            <ConfessionCard
              key={confession.id}
              confession={{ ...confession, isDaily: true }}
              onOpenComments={handleOpenComments}
            />
          ))}
        </div>
      </div>
      
      {/* Divider between featured and main feed */}
      <div className="flex items-center gap-3 mt-6 mb-2">
        <div className="flex-1 h-px bg-white/10" />
        <span className="text-xs text-gray-500">All Confessions</span>
        <div className="flex-1 h-px bg-white/10" />
      </div>

      {/* Single comments modal for DailyTop */}
      <CommentsModal
        confessionId={modalConfession?.id || ''}
        totalCount={modalConfession?.commentCount || 0}
        isOpen={modalConfession !== null}
        onClose={handleCloseComments}
      />
    </div>
  );
}
